import { FieldRow } from "../../projects/config/FieldRow";
import { SectionCard } from "../../projects/config/SectionCard";
import type { BotsChatConfig, BotsGuildLimits } from "../config/types";
import { LabelledNumber } from "./LabelledNumber";

interface Props {
  guildLimits: BotsGuildLimits;
  chat: BotsChatConfig;
  onGuildLimits: (patch: Partial<BotsGuildLimits>) => void;
  onChat: (patch: Partial<BotsChatConfig>) => void;
  dirty: boolean;
}

/**
 * `bots.guild_limits` and `bots.chat` — the ceilings that sit on top of every
 * profile, and the short reactions bots give a living player.
 *
 * One card because they are the same rule from two sides: the limits cap what
 * a guild may hear per hour, and the reactions are the traffic most likely to
 * hit that cap.
 *
 * Mockup: bots-page-shell.html §4, "Guild limits & chat reactions".
 */
export function LimitsSection({
  guildLimits,
  chat,
  onGuildLimits,
  onChat,
  dirty,
}: Props) {
  return (
    <SectionCard
      id="limits"
      title="Guild limits & chat reactions"
      apiName="bots.guild_limits · bots.chat"
      dirty={dirty}
    >
      <FieldRow
        name="Guild-level limits"
        apiName="bots.guild_limits"
        hint="Applied on top of every profile — this is what keeps a guild from turning into a bot wall."
      >
        <LabelledNumber
          label="bot msgs / hour"
          value={guildLimits.messages_per_hour}
          onChange={(n) => onGuildLimits({ messages_per_hour: n })}
        />
        <LabelledNumber
          label="gap between msgs"
          unit="s"
          value={guildLimits.gap_seconds}
          onChange={(n) => onGuildLimits({ gap_seconds: n })}
        />
        <LabelledNumber
          label="no repeat speaker within"
          unit="last messages"
          value={guildLimits.no_repeat_last}
          onChange={(n) => onGuildLimits({ no_repeat_last: n })}
        />
        <LabelledNumber
          label="phrase reuse after"
          unit="h per guild"
          value={guildLimits.phrase_reuse_hours}
          onChange={(n) => onGuildLimits({ phrase_reuse_hours: n })}
        />
      </FieldRow>

      <FieldRow
        name="Reacting to real players"
        apiName="bots.chat.reply_to_player"
        hint="A short reaction, never an answer: bots do not carry a conversation. Silence beats a wrong reply."
      >
        <LabelledNumber
          label="window"
          unit="min after the player's message"
          value={chat.reply_window_minutes}
          onChange={(n) => onChat({ reply_window_minutes: n })}
        />
        <LabelledNumber
          label="max reactions"
          unit="per 2 min per guild"
          value={chat.max_reactions_per_2min}
          onChange={(n) => onChat({ max_reactions_per_2min: n })}
        />
        <LabelledNumber
          label="thanks"
          unit={`% after ${chat.thanks_delay_min_seconds}–${chat.thanks_delay_max_seconds} s`}
          value={chat.thanks_probability}
          onChange={(n) => onChat({ thanks_probability: n })}
        />
        <LabelledNumber
          label="small talk weight"
          unit="against the help kinds"
          value={chat.small_talk_weight}
          onChange={(n) => onChat({ small_talk_weight: n })}
        />
      </FieldRow>
    </SectionCard>
  );
}
